import './style.css';
import { PRODUCTS, CATEGORIES, priceForQty, priceForQtyNgn, formatMoney, formatMoneyNgn, FREIGHT_FLAT_NGN } from './data/products.js';
import { getCurrency, setCurrency } from './lib/currency.js';
import { backendLabel, isSupabaseConfigured } from './lib/supabase.js';
import {
  getCart,
  setCart,
  saveEnquiry,
  saveOrder,
  getLocalEnquiries,
  getLocalOrders,
  fetchRemoteEnquiries,
  fetchRemoteOrders,
} from './lib/store.js';
import {
  initAuth,
  onAuthChange,
  isSignedIn,
  signIn,
  signUp,
  signOut,
  authState,
  displayName,
} from './lib/auth.js';

const FREIGHT_FLAT = FREIGHT_FLAT_NGN; // NGN inland haulage estimate
const state = {
  view: 'home',
  category: 'All',
  search: '',
  sort: 'featured',
  productId: null,
  role: 'buyer',
  sellerTab: 'enquiries',
  authTab: 'signin',
  authReturn: null,
  guestEnquire: false,
  guestCheckout: false,
};

const $ = (sel, el = document) => el.querySelector(sel);
const $$ = (sel, el = document) => [...el.querySelectorAll(sel)];

function toast(msg) {
  const el = $('#toast');
  el.textContent = msg;
  el.hidden = false;
  clearTimeout(toast._t);
  toast._t = setTimeout(() => {
    el.hidden = true;
  }, 2800);
}

function productById(id) {
  return PRODUCTS.find((p) => p.id === id);
}

function filteredProducts() {
  let list = [...PRODUCTS];
  if (state.category !== 'All') {
    list = list.filter((p) => p.category === state.category);
  }
  const q = state.search.trim().toLowerCase();
  if (q) {
    list = list.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.factory.toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q),
    );
  }
  switch (state.sort) {
    case 'price-asc':
      list.sort((a, b) => a.unitPrice - b.unitPrice);
      break;
    case 'price-desc':
      list.sort((a, b) => b.unitPrice - a.unitPrice);
      break;
    case 'moq':
      list.sort((a, b) => a.moq - b.moq);
      break;
    case 'lead':
      list.sort((a, b) => a.leadTimeDays - b.leadTimeDays);
      break;
    default:
      list.sort((a, b) => Number(b.featured) - Number(a.featured) || a.name.localeCompare(b.name));
  }
  return list;
}

/** Number of distinct cart line items (not summed MOQ qty — that leaked into the badge). */
function cartCount() {
  return getCart().length;
}

function formatBadgeCount(n) {
  if (n > 99) return '99+';
  return String(n);
}

function updateBadges() {
  const n = cartCount();
  for (const id of ['cart-badge', 'cart-badge-mobile']) {
    const el = $('#' + id);
    if (!el) continue;
    el.hidden = n === 0;
    el.textContent = formatBadgeCount(n);
  }
}

function syncCurrencySelect() {
  const sel = $('#currency-select');
  if (sel) sel.value = getCurrency();
}

function refreshPrices() {
  syncCurrencySelect();
  const name = state.view;
  if (name === 'home') renderHome();
  else if (name === 'detail') renderDetail();
  else if (name === 'cart') renderCart();
  else if (name === 'checkout') renderCheckout();
  else if (name === 'orders') renderBuyerOrders();
  else if (name === 'seller') renderSeller();
  else if (name === 'order-done') {
    /* confirmation already rendered; leave body text as-is */
  }
  updateBadges();
}

function showView(name) {
  state.view = name;
  $$('.view').forEach((v) => {
    v.hidden = v.id !== `view-${name}`;
  });
  $$('.nav-link').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.nav === name || (name === 'detail' && btn.dataset.nav === 'home') || ((name === 'enquire' || name === 'enquire-done') && btn.dataset.nav === 'home') || ((name === 'checkout' || name === 'order-done') && btn.dataset.nav === 'cart') || (name === 'auth' && btn.dataset.nav === 'auth'));
  });
  closeDrawer();
  window.scrollTo({ top: 0, behavior: 'smooth' });

  if (name === 'home') renderHome();
  if (name === 'detail') renderDetail();
  if (name === 'cart') renderCart();
  if (name === 'checkout') renderCheckout();
  if (name === 'orders') renderBuyerOrders();
  if (name === 'seller') renderSeller();
  if (name === 'auth') renderAuth();
  if (name === 'enquire') syncEnquireGate();
  updateBadges();
  updateAuthChip();
}

function closeDrawer() {
  const drawer = $('#mobile-drawer');
  const overlay = $('#drawer-overlay');
  const toggle = $('#menu-toggle');
  if (drawer) drawer.hidden = true;
  if (overlay) overlay.hidden = true;
  if (toggle) {
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Open menu');
  }
  document.body.classList.remove('drawer-open');
}

function openDrawer() {
  const drawer = $('#mobile-drawer');
  const overlay = $('#drawer-overlay');
  const toggle = $('#menu-toggle');
  if (drawer) drawer.hidden = false;
  if (overlay) overlay.hidden = false;
  if (toggle) {
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', 'Close menu');
  }
  document.body.classList.add('drawer-open');
}

function renderCategoryPills() {
  const host = $('#category-pills');
  host.innerHTML = CATEGORIES.map(
    (c) =>
      `<button type="button" class="pill${state.category === c ? ' active' : ''}" role="tab" aria-selected="${state.category === c}" data-category="${c}">${c}</button>`,
  ).join('');
}

function renderHome() {
  renderCategoryPills();
  const list = filteredProducts();
  $('#results-count').textContent = `${list.length} product${list.length === 1 ? '' : 's'} · ${backendLabel()}`;
  const grid = $('#product-grid');
  const empty = $('#home-empty');
  if (!list.length) {
    grid.innerHTML = '';
    empty.hidden = false;
    return;
  }
  empty.hidden = true;
  grid.innerHTML = list
    .map(
      (p) => `
    <button type="button" class="product-card" role="listitem" data-product-id="${p.id}" aria-label="${escapeAttr(p.name)} from ${escapeAttr(p.factory)}">
      <div class="card-visual" aria-hidden="true">
        <span class="card-cat">${escapeHtml(p.category)}</span>
        <span>${p.icon || '📦'}</span>
      </div>
      <div class="card-body">
        <h2>${escapeHtml(p.name)}</h2>
        <p class="card-factory">${escapeHtml(p.factory)}</p>
        <div class="card-meta">
          <div class="meta-item price"><span>Wholesale</span><strong>${formatMoneyNgn(priceForQtyNgn(p, p.moq))}</strong></div>
          <div class="meta-item"><span>MOQ</span><strong>${p.moq.toLocaleString()} ${p.unit}</strong></div>
          <div class="meta-item"><span>Lead time</span><strong>${p.leadTimeDays} days</strong></div>
          <div class="meta-item"><span>SKU</span><strong>${escapeHtml(p.sku)}</strong></div>
        </div>
      </div>
    </button>`,
    )
    .join('');
}

function renderDetail() {
  const p = productById(state.productId);
  const host = $('#detail-content');
  if (!p) {
    host.innerHTML = `<p>Product not found.</p><button type="button" class="btn btn-primary" data-nav="home">Back to browse</button>`;
    return;
  }
  const tierSource = p.priceTiersNgn?.length
    ? p.priceTiersNgn.map((t) => ({ minQty: t.minQty, label: formatMoneyNgn(t.price) }))
    : (p.priceTiers || []).map((t) => ({ minQty: t.minQty, label: formatMoney(t.price) }));
  const tiers = tierSource
    .map(
      (t) =>
        `<tr><td>${t.minQty.toLocaleString()}+ ${escapeHtml(p.unit)}</td><td>${t.label} / ${escapeHtml(p.unit)}</td></tr>`,
    )
    .join('');
  const specs = Object.entries(p.specs || {})
    .map(([k, v]) => `<li><span>${escapeHtml(k)}</span><strong>${escapeHtml(String(v))}</strong></li>`)
    .join('');

  host.innerHTML = `
    <div class="detail-visual" aria-hidden="true">${p.icon || '📦'}</div>
    <div class="detail-info">
      <p class="eyebrow">${escapeHtml(p.category)}</p>
      <h1 id="detail-heading">${escapeHtml(p.name)}</h1>
      <p class="detail-factory">${escapeHtml(p.factory)} · SKU ${escapeHtml(p.sku)}</p>
      <div class="detail-chips">
        <span class="chip">MOQ <strong>${p.moq.toLocaleString()} ${escapeHtml(p.unit)}</strong></span>
        <span class="chip">From <strong>${formatMoneyNgn(priceForQtyNgn(p, p.moq))}</strong></span>
        <span class="chip">Lead <strong>${p.leadTimeDays} days</strong></span>
      </div>
      <p class="detail-desc">${escapeHtml(p.description)}</p>
      <h2 class="form-section-title" style="margin:0 0 0.5rem;font-size:0.95rem">Specifications</h2>
      <ul class="specs">${specs}</ul>
      <div class="tiers">
        <h2>Price tiers</h2>
        <table class="tier-table">
          <thead><tr><th>Quantity</th><th>Unit price</th></tr></thead>
          <tbody>${tiers}</tbody>
        </table>
      </div>
      <div class="qty-row">
        <div class="field">
          <label for="detail-qty">Order quantity</label>
          <input type="number" id="detail-qty" min="${p.moq}" step="1" value="${p.moq}" />
          <p class="field-hint">Minimum order quantity: ${p.moq.toLocaleString()} ${escapeHtml(p.unit)}</p>
        </div>
      </div>
      <div class="detail-actions">
        <button type="button" class="btn btn-primary" id="btn-add-cart">Add to cart</button>
        <button type="button" class="btn btn-secondary" id="btn-enquire">Enquire</button>
      </div>
    </div>`;

  $('#btn-add-cart').addEventListener('click', () => {
    const qty = Math.max(1, parseInt($('#detail-qty').value, 10) || p.moq);
    if (qty < p.moq) {
      toast(`Quantity must meet MOQ of ${p.moq.toLocaleString()}`);
      $('#detail-qty').focus();
      return;
    }
    addToCart(p.id, qty);
    toast('Added to cart');
    updateBadges();
  });
  $('#btn-enquire').addEventListener('click', () => openEnquire(p.id));
}

function openEnquire(productId) {
  const p = productById(productId);
  if (!p) return;
  state.productId = productId;
  state.guestEnquire = false;
  $('#enquire-product-id').value = p.id;
  $('#enquire-product-summary').textContent = `${p.name} · ${p.factory} · MOQ ${p.moq.toLocaleString()} ${p.unit}`;
  $('#enq-qty').value = p.moq;
  $('#enq-qty').min = 1;
  $('#enq-moq-hint').textContent = `Factory MOQ is ${p.moq.toLocaleString()} ${p.unit}. You may enquire for other volumes.`;
  if (isSignedIn() && authState.profile?.company && !$('#enq-company').value) {
    $('#enq-company').value = authState.profile.company;
  }
  if (isSignedIn() && authState.user?.email && !$('#enq-email').value) {
    $('#enq-email').value = authState.user.email;
  }
  $('#enquire-back').onclick = () => {
    state.productId = p.id;
    showView('detail');
  };
  showView('enquire');
}

function syncEnquireGate() {
  const gate = $('#enquire-auth-gate');
  const form = $('#enquire-form');
  if (!gate || !form) return;
  const needPrompt = isSupabaseConfigured && !isSignedIn() && !state.guestEnquire;
  gate.hidden = !needPrompt;
  form.hidden = needPrompt;
}

function syncCheckoutGate() {
  const gate = $('#checkout-auth-gate');
  const form = $('#checkout-form');
  if (!gate || !form) return;
  const needPrompt = isSupabaseConfigured && !isSignedIn() && !state.guestCheckout;
  gate.hidden = !needPrompt;
  form.hidden = needPrompt;
}

function addToCart(productId, qty) {
  const cart = getCart();
  const existing = cart.find((i) => i.productId === productId);
  if (existing) existing.qty = qty;
  else cart.push({ productId, qty });
  setCart(cart);
  updateBadges();
}

function renderCart() {
  const cart = getCart();
  const empty = $('#cart-empty');
  const content = $('#cart-content');
  if (!cart.length) {
    empty.hidden = false;
    content.hidden = true;
    return;
  }
  empty.hidden = true;
  content.hidden = false;

  let subtotal = 0;
  let moqOk = true;
  const lines = $('#cart-lines');
  lines.innerHTML = cart
    .map((item) => {
      const p = productById(item.productId);
      if (!p) return '';
      const unit = priceForQtyNgn(p, item.qty);
      const line = unit * item.qty;
      subtotal += line;
      const below = item.qty < p.moq;
      if (below) moqOk = false;
      return `
      <article class="cart-line" role="listitem" data-cart-id="${p.id}">
        <div class="cart-line-top">
          <div>
            <h3>${escapeHtml(p.name)}</h3>
            <p>${escapeHtml(p.factory)} · ${escapeHtml(p.sku)}</p>
          </div>
          <button type="button" class="btn btn-danger-ghost" data-remove="${p.id}">Remove</button>
        </div>
        <div class="cart-line-controls">
          <div class="qty-control">
            <label for="qty-${p.id}">Qty</label>
            <input type="number" id="qty-${p.id}" data-qty="${p.id}" min="${p.moq}" step="1" value="${item.qty}" />
          </div>
          <div class="line-total">${formatMoneyNgn(line)}</div>
          ${below ? `<p class="moq-warn">Below MOQ (${p.moq.toLocaleString()} ${escapeHtml(p.unit)})</p>` : `<p class="field-hint" style="width:100%;margin:0">Unit ${formatMoneyNgn(unit)} · MOQ ${p.moq.toLocaleString()}</p>`}
        </div>
      </article>`;
    })
    .join('');

  $('#cart-subtotal').textContent = formatMoneyNgn(subtotal);
  const checkoutBtn = content.querySelector('[data-nav="checkout"]');
  if (checkoutBtn) {
    checkoutBtn.disabled = !moqOk;
    checkoutBtn.title = moqOk ? '' : 'Fix quantities to meet MOQ before checkout';
  }
}

function renderCheckout() {
  const cart = getCart();
  if (!cart.length) {
    showView('cart');
    return;
  }
  let merchandise = 0;
  for (const item of cart) {
    const p = productById(item.productId);
    if (!p) continue;
    if (item.qty < p.moq) {
      toast('Adjust cart quantities to meet MOQ');
      showView('cart');
      return;
    }
    merchandise += priceForQtyNgn(p, item.qty) * item.qty;
  }
  const freight = FREIGHT_FLAT;
  $('#co-merchandise').textContent = formatMoneyNgn(merchandise);
  $('#co-freight').textContent = formatMoneyNgn(freight);
  $('#co-total').textContent = formatMoneyNgn(merchandise + freight);

  if (isSignedIn()) {
    if (authState.profile?.company && !$('#co-company').value) {
      $('#co-company').value = authState.profile.company;
    }
    if (authState.user?.email && !$('#co-email').value) {
      $('#co-email').value = authState.user.email;
    }
  }
  syncCheckoutGate();
}

async function renderBuyerOrders() {
  const orders = getLocalOrders();
  const host = $('#orders-list');
  const empty = $('#orders-empty');
  if (!orders.length) {
    host.innerHTML = '';
    empty.hidden = false;
    return;
  }
  empty.hidden = true;
  host.innerHTML = orders.map(orderCardHtml).join('');
}

async function renderSeller() {
  const enquiries = await fetchRemoteEnquiries().catch(() => getLocalEnquiries());
  const orders = await fetchRemoteOrders().catch(() => getLocalOrders());

  const enqBadge = $('#enq-count-badge');
  const ordBadge = $('#ord-count-badge');
  enqBadge.hidden = !enquiries.length;
  enqBadge.textContent = String(enquiries.length);
  ordBadge.hidden = !orders.length;
  ordBadge.textContent = String(orders.length);

  const showEnq = state.sellerTab === 'enquiries';
  $('#seller-enquiries').hidden = !showEnq;
  $('#seller-orders').hidden = showEnq;
  $$('.seller-tab').forEach((t) => {
    const on = t.dataset.sellerTab === state.sellerTab;
    t.classList.toggle('active', on);
    t.setAttribute('aria-selected', String(on));
  });

  const enqList = $('#seller-enq-list');
  const enqEmpty = $('#seller-enq-empty');
  if (!enquiries.length) {
    enqList.innerHTML = '';
    enqEmpty.hidden = false;
  } else {
    enqEmpty.hidden = true;
    enqList.innerHTML = enquiries
      .map(
        (e) => `
      <article class="data-card">
        <header>
          <h3>${escapeHtml(e.productName)}</h3>
          <span class="status">${escapeHtml(e.status || 'new')}</span>
        </header>
        <p class="meta">${formatDate(e.createdAt)} · ${escapeHtml(e.id?.slice?.(0, 8) || '')}</p>
        <dl>
          <div><dt>Factory</dt><dd>${escapeHtml(e.factoryName || '—')}</dd></div>
          <div><dt>Company</dt><dd>${escapeHtml(e.company)}</dd></div>
          <div><dt>Contact</dt><dd>${escapeHtml(e.contact)} · ${escapeHtml(e.email)}</dd></div>
          <div><dt>Quantity</dt><dd>${Number(e.quantity).toLocaleString()}</dd></div>
        </dl>
        <div class="message-block">${escapeHtml(e.message)}</div>
      </article>`,
      )
      .join('');
  }

  const ordList = $('#seller-ord-list');
  const ordEmpty = $('#seller-ord-empty');
  if (!orders.length) {
    ordList.innerHTML = '';
    ordEmpty.hidden = false;
  } else {
    ordEmpty.hidden = true;
    ordList.innerHTML = orders.map(orderCardHtml).join('');
  }
}

function orderCardHtml(o) {
  const items = (o.items || [])
    .map((it) => `${escapeHtml(it.name)} × ${it.qty.toLocaleString()}`)
    .join('; ');
  return `
    <article class="data-card">
      <header>
        <h3>${escapeHtml(o.id)}</h3>
        <span class="status">${escapeHtml(o.status || 'placed')}</span>
      </header>
      <p class="meta">${formatDate(o.createdAt)}</p>
      <dl>
        <div><dt>Buyer</dt><dd>${escapeHtml(o.company)} · ${escapeHtml(o.contact)}</dd></div>
        <div><dt>Email</dt><dd>${escapeHtml(o.email)}</dd></div>
        <div><dt>Ship to</dt><dd>${escapeHtml(o.address || '—')}</dd></div>
        <div><dt>Items</dt><dd>${items || '—'}</dd></div>
        <div><dt>Total</dt><dd>${formatMoneyNgn(o.total || 0)}</dd></div>
        ${o.notes ? `<div><dt>Notes</dt><dd>${escapeHtml(o.notes)}</dd></div>` : ''}
      </dl>
    </article>`;
}


function updateAuthChip() {
  const chip = $('#auth-chip');
  const drawer = $('#drawer-auth');
  const label = isSignedIn() ? displayName() || 'Account' : 'Sign in';
  if (chip) chip.textContent = label;
  if (drawer) drawer.textContent = isSignedIn() ? 'Account' : 'Sign in';
}

function renderAuth() {
  const signedIn = $('#auth-signed-in');
  const signedOut = $('#auth-signed-out');
  const lead = $('#auth-lead');
  if (!isSupabaseConfigured) {
    if (lead) {
      lead.textContent =
        'Supabase is not configured. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to enable accounts.';
    }
    if (signedIn) signedIn.hidden = true;
    if (signedOut) signedOut.hidden = true;
    return;
  }
  if (isSignedIn()) {
    if (lead) lead.textContent = 'You are signed in. Enquiries and orders sync with your account.';
    if (signedIn) signedIn.hidden = false;
    if (signedOut) signedOut.hidden = true;
    $('#auth-email').textContent = authState.user?.email || '—';
    $('#auth-role').textContent = authState.profile?.role || authState.user?.user_metadata?.role || 'buyer';
    $('#auth-company').textContent = authState.profile?.company || '—';
  } else {
    if (lead) {
      lead.textContent = state.authReturn
        ? `Sign in to continue to ${state.authReturn}, or create an account.`
        : 'Sign in or create a ForgeLink account to sync enquiries and orders.';
    }
    if (signedIn) signedIn.hidden = true;
    if (signedOut) signedOut.hidden = false;
    const showSignIn = state.authTab !== 'signup';
    $('#signin-form').hidden = !showSignIn;
    $('#signup-form').hidden = showSignIn;
    $$('[data-auth-tab]').forEach((t) => {
      const on = t.dataset.authTab === (showSignIn ? 'signin' : 'signup');
      t.classList.toggle('active', on);
      t.setAttribute('aria-selected', String(on));
    });
  }
}

function afterAuthSuccess() {
  const ret = state.authReturn;
  state.authReturn = null;
  state.guestEnquire = false;
  state.guestCheckout = false;
  updateAuthChip();
  if (ret === 'enquire' && state.productId) {
    openEnquire(state.productId);
    return;
  }
  if (ret === 'checkout') {
    showView('checkout');
    return;
  }
  if (authState.profile?.role === 'factory') {
    state.role = 'seller';
    syncRoleUI();
    showView('seller');
    return;
  }
  showView('home');
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'America/New_York',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(str) {
  return escapeHtml(str).replace(/'/g, '&#39;');
}

function bindEvents() {
  document.body.addEventListener('click', (e) => {
    const nav = e.target.closest('[data-nav]');
    if (nav) {
      e.preventDefault();
      const target = nav.dataset.nav;
      if (target === 'seller') {
        state.role = 'seller';
        syncRoleUI();
      }
      if (target === 'checkout') {
        state.guestCheckout = false;
      }
      if (target === 'auth' && nav.dataset.authReturn) {
        state.authReturn = nav.dataset.authReturn;
      }
      if (target === 'detail') return;
      showView(target);
      return;
    }

    const card = e.target.closest('[data-product-id]');
    if (card && state.view === 'home') {
      state.productId = card.dataset.productId;
      showView('detail');
      return;
    }

    const pill = e.target.closest('[data-category]');
    if (pill) {
      state.category = pill.dataset.category;
      renderHome();
      return;
    }

    const remove = e.target.closest('[data-remove]');
    if (remove) {
      setCart(getCart().filter((i) => i.productId !== remove.dataset.remove));
      renderCart();
      updateBadges();
      toast('Removed from cart');
      return;
    }

    const sellerTab = e.target.closest('[data-seller-tab]');
    if (sellerTab) {
      state.sellerTab = sellerTab.dataset.sellerTab;
      renderSeller();
      return;
    }

    const authTab = e.target.closest('[data-auth-tab]');
    if (authTab) {
      state.authTab = authTab.dataset.authTab;
      renderAuth();
      return;
    }

    const authNav = e.target.closest('[data-nav="auth"]');
    if (authNav && authNav.dataset.authReturn) {
      state.authReturn = authNav.dataset.authReturn;
    }

    const roleBtn = e.target.closest('[data-role]');
    if (roleBtn) {
      state.role = roleBtn.dataset.role;
      syncRoleUI();
      showView(state.role === 'seller' ? 'seller' : 'home');
    }
  });

  document.body.addEventListener('change', (e) => {
    const qtyInput = e.target.closest('[data-qty]');
    if (qtyInput) {
      const id = qtyInput.dataset.qty;
      const qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      const cart = getCart();
      const item = cart.find((i) => i.productId === id);
      if (item) {
        item.qty = qty;
        setCart(cart);
        renderCart();
        updateBadges();
      }
    }
  });

  $('#search-input').addEventListener('input', (e) => {
    state.search = e.target.value;
    renderHome();
  });
  $('#sort-select').addEventListener('change', (e) => {
    state.sort = e.target.value;
    renderHome();
  });

  $('#menu-toggle').addEventListener('click', () => {
    const open = $('#mobile-drawer').hidden;
    if (open) openDrawer();
    else closeDrawer();
  });
  $('#drawer-overlay').addEventListener('click', closeDrawer);
  $('#drawer-close')?.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !$('#mobile-drawer')?.hidden) closeDrawer();
  });

  const currencySelect = $('#currency-select');
  if (currencySelect) {
    currencySelect.value = getCurrency();
    currencySelect.addEventListener('change', () => {
      setCurrency(currencySelect.value);
      refreshPrices();
      toast(`Currency: ${currencySelect.value}`);
    });
  }

  $('#enquire-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const p = productById($('#enquire-product-id').value);
    if (!p) return;
    const company = $('#enq-company').value.trim();
    const contact = $('#enq-contact').value.trim();
    const email = $('#enq-email').value.trim();
    const phone = $('#enq-phone').value.trim();
    const quantity = parseInt($('#enq-qty').value, 10);
    const message = $('#enq-message').value.trim();
    if (!company || !contact || !email || !message || !quantity || quantity < 1) {
      toast('Please complete all required fields');
      return;
    }
    const saved = await saveEnquiry({
      productId: p.id,
      productName: p.name,
      factoryName: p.factory,
      company,
      contact,
      email,
      phone,
      quantity,
      message,
    });
    $('#enquire-done-body').textContent = `Thanks, ${contact}. Your enquiry about “${p.name}” (${quantity.toLocaleString()} ${p.unit}) was saved. Reference ${saved.id.slice(0, 8)}. The factory can follow up at ${email}.`;
    e.target.reset();
    showView('enquire-done');
  });

  $('#checkout-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const cart = getCart();
    if (!cart.length) {
      showView('cart');
      return;
    }
    const company = $('#co-company').value.trim();
    const contact = $('#co-contact').value.trim();
    const email = $('#co-email').value.trim();
    const phone = $('#co-phone').value.trim();
    const address = $('#co-address').value.trim();
    const notes = $('#co-notes').value.trim();
    if (!company || !contact || !email || !address) {
      toast('Please complete all required checkout fields');
      return;
    }

    const items = [];
    let merchandise = 0;
    for (const line of cart) {
      const p = productById(line.productId);
      if (!p) continue;
      if (line.qty < p.moq) {
        toast(`“${p.name}” is below MOQ`);
        showView('cart');
        return;
      }
      const unitPrice = priceForQtyNgn(p, line.qty);
      const lineTotal = unitPrice * line.qty;
      merchandise += lineTotal;
      items.push({
        productId: p.id,
        name: p.name,
        factory: p.factory,
        sku: p.sku,
        unitPrice,
        qty: line.qty,
        lineTotal,
      });
    }
    const freight = FREIGHT_FLAT;
    const order = await saveOrder({
      company,
      contact,
      email,
      phone,
      address,
      notes,
      merchandise,
      freight,
      total: merchandise + freight,
      items,
    });
    $('#order-done-body').textContent = `Thank you, ${contact}. Your wholesale order for ${formatMoneyNgn(order.total)} has been recorded for ${company}.`;
    $('#order-id-code').textContent = order.id;
    e.target.reset();
    updateBadges();
    showView('order-done');
  });

  const enquireGuest = $('#enquire-continue-guest');
  if (enquireGuest) {
    enquireGuest.addEventListener('click', () => {
      state.guestEnquire = true;
      syncEnquireGate();
    });
  }
  const checkoutGuest = $('#checkout-continue-guest');
  if (checkoutGuest) {
    checkoutGuest.addEventListener('click', () => {
      state.guestCheckout = true;
      syncCheckoutGate();
    });
  }

  $('#signin-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errEl = $('#signin-error');
    errEl.hidden = true;
    try {
      await signIn({
        email: $('#si-email').value.trim(),
        password: $('#si-password').value,
      });
      toast('Signed in');
      afterAuthSuccess();
    } catch (err) {
      errEl.textContent = err.message || 'Sign in failed';
      errEl.hidden = false;
    }
  });

  $('#signup-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errEl = $('#signup-error');
    errEl.hidden = true;
    const roleInput = document.querySelector('input[name="su-role"]:checked');
    try {
      const result = await signUp({
        email: $('#su-email').value.trim(),
        password: $('#su-password').value,
        role: roleInput?.value === 'factory' ? 'factory' : 'buyer',
        company: $('#su-company').value.trim(),
      });
      if (result.session) {
        toast('Account created');
        afterAuthSuccess();
      } else {
        toast('Check your email to confirm, then sign in');
        state.authTab = 'signin';
        renderAuth();
      }
    } catch (err) {
      errEl.textContent = err.message || 'Sign up failed';
      errEl.hidden = false;
    }
  });

  $('#btn-sign-out')?.addEventListener('click', async () => {
    try {
      await signOut();
      toast('Signed out');
      renderAuth();
      updateAuthChip();
    } catch (err) {
      toast(err.message || 'Sign out failed');
    }
  });
}

function syncRoleUI() {
  $$('.role-btn').forEach((b) => b.classList.toggle('active', b.dataset.role === state.role));
}

async function init() {
  bindEvents();
  syncRoleUI();
  syncCurrencySelect();
  updateBadges();
  await initAuth();
  onAuthChange(() => {
    updateAuthChip();
    if (state.view === 'auth') renderAuth();
    if (state.view === 'enquire') syncEnquireGate();
    if (state.view === 'checkout') syncCheckoutGate();
    if (isSignedIn() && authState.profile?.role === 'factory' && state.role !== 'seller') {
      /* do not force role switch mid-browse */
    }
  });
  updateAuthChip();
  showView('home');
  console.info(`[ForgeLink] Backend: ${backendLabel()}`);
}

init();
